import React, { useEffect, useRef, useState } from 'react';
import { css } from '@emotion/react';
import { posthog } from 'posthog-js';
import { useHistory, useParams } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { useTranslation } from 'react-i18next';
import { AppBar } from '../Components/AppBar';
import { Slide } from '../Types/Types';
import { OnboardingButton } from '../Components/OnboardingButton';
import { colors, fontStyles, spacing, safeAreaInset } from '../../../styles';

import * as stores from '../../../stores';
import * as storage from '../../../storage';
import { InvestorTypeSelector } from '../Components/InvestorTypeSelector';
import { OnboardingErrorBoundary } from '../Components/OnboardingErrorBoundary';
import OnboardingNotification from '../Components/OnboardingNotification';
import TryBloomStep from '../Components/TryBloomStep';
import OnboardingAIArenaTeaser from '../Components/OnboardingAIArenaTeaser';
import OnboardingTopIdeas from '../Components/OnboardingTopIdeas';
import { InvestorType } from '../../../utils/smartChatPrompts';
import { trackTryBloomSkipped } from '../../../utils/analytics';
import { useNotificationPreferences } from '../../../hooks/useNotificationPreferences';
import { AUTO_CHAT_PROMPTS } from '../../../constants/onboarding';

// Utility function to highlight specific words in text
// Highlight specific words in text using indexOf (no lookbehind — iOS 15 compat)
const highlightWords = (
  text: string,
  wordsToHighlight: string[] = [],
  color: string = colors.petrolBlue,
): React.ReactNode => {
  if (!wordsToHighlight || wordsToHighlight.length === 0) {
    return text;
  }

  const lowerText = text.toLowerCase();
  // Find all match positions for each highlight word
  const matches: { start: number; end: number }[] = [];
  for (const word of wordsToHighlight) {
    const lowerWord = word.toLowerCase();
    let pos = lowerText.indexOf(lowerWord);
    while (pos !== -1) {
      matches.push({ start: pos, end: pos + word.length });
      pos = lowerText.indexOf(lowerWord, pos + 1);
    }
  }

  if (matches.length === 0) return text;

  // Sort by start position and merge overlapping ranges
  matches.sort((a, b) => a.start - b.start);
  const merged = [matches[0]];
  for (let i = 1; i < matches.length; i++) {
    const last = merged[merged.length - 1];
    if (matches[i].start <= last.end) {
      last.end = Math.max(last.end, matches[i].end);
    } else {
      merged.push(matches[i]);
    }
  }

  // Build React nodes from the merged ranges
  const result: React.ReactNode[] = [];
  let cursor = 0;
  for (const { start, end } of merged) {
    if (cursor < start) result.push(text.slice(cursor, start));
    result.push(
      <span
        key={start}
        css={css`
          color: ${color};
        `}
      >
        {text.slice(start, end)}
      </span>,
    );
    cursor = end;
  }
  if (cursor < text.length) result.push(text.slice(cursor));

  return result;
};

export const OnboardingView: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { step } = useParams<{ step?: string }>();
  const index = step ? parseInt(step, 10) : 0;
  const [isDarkMode, setIsDarkMode] = useState(document.documentElement.getAttribute('data-theme') === 'dark');
  const [userSymbols, setUserSymbols] = useState<string[]>([]);
  const [userMotivations, setUserMotivations] = useState<InvestorType[]>([]);
  const [tryBloomState, setTryBloomState] = useState({ hasAskedQuestion: false, hasResponse: false });
  const { preferences: notificationPreferences } = useNotificationPreferences();
  const hasAnyAlertEnabled = notificationPreferences.some((pref) => pref.enabled);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    localStorage.setItem('onboardingIndex', index.toString());

    // Save onboarding start time on first step
    if (index === 0 && !localStorage.getItem('onboardingStartTime')) {
      localStorage.setItem('onboardingStartTime', Date.now().toString());
    }

    posthog.capture('Onboarding Step Viewed', { step: index });
  }, [index]);

  // Handle stock input completion
  const handleStockInputComplete = async () => {
    // Add selected stocks to "Watchlist" portfolio
    if (userSymbols.length > 0) {
      try {
        const portfolios = await storage.getPortfolios();
        const existingWatchlist = portfolios.find((p) => p.title === 'Watchlist' || p.title === 'My Watchlist');

        if (existingWatchlist) {
          // Update existing "Watchlist" portfolio with new symbols
          const updatedPortfolio = {
            ...existingWatchlist,
            symbols: [...new Set([...existingWatchlist.symbols, ...userSymbols])], // Merge and deduplicate
          };
          await storage.updatePortfolio(updatedPortfolio);
        } else {
          // Create new "Watchlist" portfolio
          await storage.savePortfolio({
            title: 'Watchlist',
            strategy: 'Default watchlist',
            symbols: userSymbols,
            allocations: [],
          });
        }
      } catch (error) {
        console.error('Error adding symbols to watchlist portfolio:', error);
        // Fallback to old method if portfolio system fails
        stores.General.update((s) => {
          s.watchlist = { ...s.watchlist, ...Object.fromEntries(userSymbols.map((symbol) => [symbol, true])) };
        });
      }
    }

    // Navigate to chat demo
    history.push(`/onboarding/${index + 1}`);
  };

  // Handle chat demo completion and navigate to alerts setup
  const handleChatComplete = () => {
    // Navigate to alerts setup slide
    history.push(`/onboarding/${index + 1}`);
  };

  // Handle alerts setup completion - show paywall then redirect to chat
  const handleAlertsComplete = () => {
    // Show paywall modal (hasCompletedOnboarding will be set after paywall interaction)
    // The paywall modal will handle the final redirect to chat tab
    stores.showPaymentModalByPreference('onboarding_completion');
  };

  useEffect(() => {
    const handleThemeChange = () => {
      setIsDarkMode(document.documentElement.getAttribute('data-theme') === 'dark');
    };

    handleThemeChange();

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'data-theme') {
          handleThemeChange();
        }
      });
    });

    observer.observe(document.documentElement, { attributes: true });

    return () => {
      observer.disconnect();
    };
  }, []);

  // Handle motivation selection without auto-navigation
  const handleMotivationSelect = (motivations: InvestorType[]) => {
    setUserMotivations(motivations);
    // Save motivations to localStorage for tracking on completion
    localStorage.setItem('onboardingMotivations', JSON.stringify(motivations));
  };

  // Derive top stock from user's selections (used to personalize screens 5 & 6)
  const topSymbol = userSymbols.length > 0 ? userSymbols[0] : null;

  const baseSlides: Slide[] = [
    // Screen 2 — Investor style
    {
      id: 'motivation-selector',
      title: t('onboarding.slideTitle1'),
      highlightedWords: [t('onboarding.slideHighlight1')],
      description: t('onboarding.slideDesc1'),
      content: (
        <OnboardingErrorBoundary>
          <InvestorTypeSelector onSelect={handleMotivationSelect} selected={userMotivations} />
        </OnboardingErrorBoundary>
      ),
    },
    // Screen 3 — AI portfolios performance (NEW)
    {
      id: 'ai-arena-teaser',
      title: t('onboarding.slideTitle3AI'),
      highlightedWords: [t('onboarding.slideHighlight3AI')],
      description: t('onboarding.slideDesc3AI'),
      content: (
        <OnboardingErrorBoundary>
          <OnboardingAIArenaTeaser />
        </OnboardingErrorBoundary>
      ),
    },
    // Screen 4 — Top ideas for you (NEW)
    {
      id: 'top-ideas',
      title: (() => {
        switch (userMotivations[0]) {
          case 'new-investor':
            return t('onboarding.topIdeasTitleNewInvestor');
          case 'active-trader':
            return t('onboarding.topIdeasTitleActiveTrader');
          case 'researcher':
            return t('onboarding.topIdeasTitleResearcher');
          default:
            return t('onboarding.topIdeasTitleDefault');
        }
      })(),
      highlightedWords: [t('onboarding.slideHighlight4Ideas')],
      description: t('onboarding.slideDesc4Ideas'),
      content: (
        <OnboardingErrorBoundary>
          <OnboardingTopIdeas
            investorType={userMotivations[0]}
            userSymbols={userSymbols}
            onSymbolsChange={setUserSymbols}
          />
        </OnboardingErrorBoundary>
      ),
    },
    // Screen 5 — AI chat (intentionally uses generic copy — personalized keys tested worse, see PR description)
    {
      id: 'onboarding-chat',
      title: topSymbol ? t('onboarding.slideTitle5Chat', { ticker: topSymbol }) : t('onboarding.slideTitle3'),
      highlightedWords: topSymbol ? [topSymbol] : [t('onboarding.slideHighlight3')],
      description: topSymbol ? t('onboarding.slideDesc5ChatAI') : t('onboarding.slideDesc3'),
      content: (
        <OnboardingErrorBoundary>
          <TryBloomStep
            isDarkMode={isDarkMode}
            userSymbols={userSymbols}
            onStateChange={setTryBloomState}
            initialSuggestedQuestion={topSymbol ? AUTO_CHAT_PROMPTS.aiPortfolioQuestion(topSymbol) : undefined}
          />
        </OnboardingErrorBoundary>
      ),
    },
    // Screen 6 — Alerts (personalized to top stock)
    {
      id: 'smart-alerts',
      title: topSymbol ? t('onboarding.slideTitle6Alerts', { ticker: topSymbol }) : t('onboarding.slideTitle4'),
      highlightedWords: topSymbol ? [topSymbol] : [t('onboarding.slideHighlight4')],
      description: t('onboarding.slideDesc4'),
      content: (
        <OnboardingErrorBoundary>
          <OnboardingNotification topSymbol={topSymbol ?? undefined} />
        </OnboardingErrorBoundary>
      ),
    },
  ];

  const currentSlides = baseSlides;

  // Add bounds checking to prevent crashes
  const isValidIndex = index >= 0 && index < currentSlides.length;
  const currentSlide = isValidIndex ? currentSlides[index] : null;

  // Scroll to top when visiting certain slides
  useEffect(() => {
    if (currentSlide?.id === 'smart-alerts' && scrollContainerRef.current?.scrollTo) {
      scrollContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [currentSlide?.id]);

  // Show loading state if slides are being generated (for future use)
  // const isLoading = slidesLoading && index >= 5; // Only show loading for motivation slides

  const handleNext = async () => {
    const currentSlide = currentSlides[index];

    // Handle different slide-specific logic
    if (currentSlide?.id === 'top-ideas') {
      await handleStockInputComplete();
      return;
    }

    if (currentSlide?.id === 'onboarding-chat') {
      if (!tryBloomState.hasAskedQuestion) {
        trackTryBloomSkipped({ hadInteraction: false });
      }
      handleChatComplete();
      return;
    }

    if (currentSlide?.id === 'smart-alerts') {
      handleAlertsComplete();
      return;
    }

    // Standard navigation for other slides
    if (index < currentSlides.length - 1) {
      history.push(`/onboarding/${index + 1}`);
    }
  };

  const handleBack = () => {
    if (index === 0) {
      history.push('/welcome');
    } else if (index > 0) {
      history.push(`/onboarding/${index - 1}`);
    } else {
      history.goBack();
    }
  };

  // All slides now use the main OnboardingView UI

  // If no valid slide, redirect to first slide
  if (!currentSlide) {
    history.push('/onboarding/0');
    return null;
  }

  // Use the main OnboardingView layout structure matching reference implementation
  return (
    <div
      css={css`
        width: 100%;
        max-width: 1024px;
        height: 100vh;
        margin: 0 auto;
        position: relative;
        display: flex;
        flex-direction: column;
        background-color: ${isDarkMode ? colors.liquorice : '#fffefc'};
      `}
    >
      {/* HEADER - AppBar Fixed Top */}
      <div
        css={css`
          flex-shrink: 0;
          background-color: ${isDarkMode ? colors.liquorice : '#fffefc'};
          padding-top: ${Capacitor.isNativePlatform() ? safeAreaInset.top : '0px'};
          z-index: 10;
        `}
      >
        <AppBar
          onBack={handleBack}
          showBackButton={true}
          showProgress={index >= 0}
          progressIndex={index}
          totalSteps={currentSlides.length}
        />
      </div>

      {/* BODY - Scrolleable Content */}
      <div
        ref={scrollContainerRef}
        css={css`
          flex: 1;
          overflow-y: auto;
          -webkit-overflow-scrolling: touch;
          padding: ${spacing.deci};
          display: flex;
          flex-direction: column;
        `}
      >
        {/* Title and Description */}
        <div
          css={css`
            margin-bottom: ${spacing.deci};
          `}
        >
          <h1
            css={css`
              ${fontStyles.shark};
              color: ${colors.liquorice};
              margin-bottom: ${spacing.deci};
              font-size: 26px;
              line-height: 2.5rem;
              html[data-theme='dark'] & {
                color: ${colors.sugar};
              }
            `}
          >
            {currentSlide.displayTitle ||
              highlightWords(currentSlide.title, currentSlide.highlightedWords, currentSlide.highlightColor)}
          </h1>
          <p
            css={css`
              ${fontStyles.dog};
              color: ${colors.liquorice};
              white-space: pre-line;
              margin: 0;
              margin-bottom: ${spacing.milli};
              html[data-theme='dark'] & {
                color: ${colors.sugar};
              }
            `}
          >
            {currentSlide.description}
          </p>
        </div>

        {/* Content Area */}
        <div
          css={css`
            flex: 1;
            display: flex;
            flex-direction: column;
            justify-content: flex-start;
          `}
        >
          {currentSlide.content && <div>{currentSlide.content}</div>}

          {!currentSlide.content && (
            <div
              css={css`
                display: flex;
                justify-content: center;
                align-items: center;
                flex: 1;
              `}
            >
              <img
                key={currentSlide.id}
                src={currentSlide.image || ''}
                alt={currentSlide.title}
                css={css`
                  width: 100%;
                  max-width: 400px;
                  height: auto;
                  object-fit: contain;
                `}
              />
            </div>
          )}
        </div>
      </div>

      {/* FOOTER - Button Fixed Bottom */}
      <div
        css={css`
          flex-shrink: 0;
          padding: 16px;
          padding-bottom: ${Capacitor.isNativePlatform() ? `calc(16px + ${safeAreaInset.bottom})` : '16px'};
          border-top: 0.8px solid ${colors.cloud};
          background-color: ${isDarkMode ? colors.liquorice : colors.sugar};
          z-index: 10;
          box-shadow: 0px -2px 10px rgba(0, 0, 0, 0.05);
        `}
      >
        <OnboardingButton
          onClick={handleNext}
          disabled={
            (currentSlide?.id === 'top-ideas' && userSymbols.length === 0) ||
            (currentSlide?.id === 'motivation-selector' && userMotivations.length === 0) ||
            (currentSlide?.id === 'onboarding-chat' && !tryBloomState.hasAskedQuestion) ||
            (currentSlide?.id === 'smart-alerts' && !hasAnyAlertEnabled)
          }
        >
          {currentSlide?.id === 'motivation-selector'
            ? t('onboarding.continue')
            : currentSlide?.id === 'ai-arena-teaser'
              ? t('onboarding.aiArenaCTA')
              : currentSlide?.id === 'top-ideas'
                ? t('onboarding.topIdeasCTA')
                : currentSlide?.id === 'onboarding-chat'
                  ? t('onboarding.continue')
                  : currentSlide?.id === 'smart-alerts'
                    ? t('onboarding.finishSetup')
                    : t('onboarding.next')}
        </OnboardingButton>

        {/* Skip link for motivation selector */}
        {currentSlide?.id === 'motivation-selector' && (
          <button
            onClick={handleNext}
            css={css`
              background: none;
              border: none;
              color: ${colors.silver};
              ${fontStyles.cat};
              cursor: pointer;
              padding: ${spacing.centi};
              margin-top: ${spacing.milli};
              text-decoration: underline;
              width: 100%;
              text-align: center;

              &:hover {
                color: ${colors.petrolBlue};
              }

              html[data-theme='dark'] & {
                &:hover {
                  color: ${colors.skyBlue};
                }
              }
            `}
          >
            {t('onboarding.skipNotSure')}
          </button>
        )}

        {/* Skip link for top-ideas */}
        {currentSlide?.id === 'top-ideas' && (
          <button
            onClick={handleNext}
            css={css`
              background: none;
              border: none;
              color: ${colors.silver};
              ${fontStyles.cat};
              cursor: pointer;
              padding: ${spacing.centi};
              margin-top: ${spacing.milli};
              text-decoration: underline;
              width: 100%;
              text-align: center;

              &:hover {
                color: ${colors.petrolBlue};
              }

              html[data-theme='dark'] & {
                &:hover {
                  color: ${colors.skyBlue};
                }
              }
            `}
          >
            {t('onboarding.skipAddLater')}
          </button>
        )}

        {/* Skip link for onboarding-chat */}
        {currentSlide?.id === 'onboarding-chat' && (
          <button
            onClick={handleNext}
            css={css`
              background: none;
              border: none;
              color: ${colors.silver};
              ${fontStyles.cat};
              cursor: pointer;
              padding: ${spacing.centi};
              margin-top: ${spacing.milli};
              text-decoration: underline;
              width: 100%;
              text-align: center;

              &:hover {
                color: ${colors.petrolBlue};
              }

              html[data-theme='dark'] & {
                &:hover {
                  color: ${colors.skyBlue};
                }
              }
            `}
          >
            {t('onboarding.skipForNow')}
          </button>
        )}

        {/* Skip link for smart-alerts */}
        {currentSlide?.id === 'smart-alerts' && (
          <button
            onClick={handleNext}
            css={css`
              background: none;
              border: none;
              color: ${colors.silver};
              ${fontStyles.cat};
              cursor: pointer;
              padding: ${spacing.centi};
              margin-top: ${spacing.milli};
              text-decoration: underline;
              width: 100%;
              text-align: center;

              &:hover {
                color: ${colors.petrolBlue};
              }

              html[data-theme='dark'] & {
                &:hover {
                  color: ${colors.skyBlue};
                }
              }
            `}
          >
            {t('onboarding.skipAlertsLater')}
          </button>
        )}
      </div>
    </div>
  );
};
